import { useDebounce, useToggle } from 'ahooks'
import { Avatar, Checkbox, Input, List, Modal } from 'antd'
import { getOperationName } from 'apollo-link'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DepartmentTree } from '@/components'
import { GetGroupFollowersDocument, Group, useGetUserListQuery, useGroupInviteMutation } from '@/generated/graphql'

import s from './invite-user.module.less'

interface IdsState {
  departmentIds: number[]
  userIds: number[]
}

interface DepartmentWithSearchProps {
  isPage?: boolean
  ids: IdsState
  onUpdateIds: (ids: IdsState) => void
}

export const DepartmentWithSearch: React.FC<DepartmentWithSearchProps> = ({ isPage = false, ids, onUpdateIds }) => {
  const { t } = useTranslation()
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebounce(search, { wait: 600 })

  const { data, loading, error } = useGetUserListQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !search,
    variables: {
      eventId: 0,
      userId: 0,
      groupId: 0,
      postId: 0,
      withAllUsers: true,
      filter: {
        search,
      },
      pagination: {
        first: 8,
      },
    },
  })

  if (error) {
    return null
  }

  const onClickUser = (userId: number) => {
    onUpdateIds({
      departmentIds: ids.departmentIds,
      userIds: ids.userIds.includes(userId) ? ids.userIds.filter((id) => id !== userId) : ids.userIds.concat([userId]),
    })
  }

  const users = data?.users!.edges || []

  const usersList = (
    <List
      loading={loading}
      dataSource={users}
      split={false}
      renderItem={({ node: u }) => (
        <List.Item
          className={s.listItem}
          onClick={() => onClickUser(u.userId)}
          actions={[<Checkbox key='checkbox' checked={ids.userIds.includes(u.userId)} />]}
        >
          <List.Item.Meta
            avatar={<Avatar src={u.avatarUrl} />}
            title={u.name}
            description={u.departments.map((d) => d.name).join(' - ')}
          />
        </List.Item>
      )}
    />
  )

  const content = debouncedSearch ? (
    usersList
  ) : (
    <div className={s.tree}>
      <DepartmentTree isPage={isPage} onCheck={(departmentIds, userIds) => onUpdateIds({ departmentIds, userIds })} />
    </div>
  )

  return (
    <>
      <Input.Search
        className={s.search}
        allowClear
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('search.searchUser')}
      />
      {content}
    </>
  )
}

export const InviteUser: React.FC<{ group: Group }> = ({ group, children }) => {
  const [visible, { toggle }] = useToggle()
  const { t } = useTranslation()

  const [invite] = useGroupInviteMutation({
    refetchQueries: [getOperationName(GetGroupFollowersDocument)!],
  })

  const [ids, setIds] = React.useState<IdsState>({
    departmentIds: [],
    userIds: [],
  })

  const onClose = () => {
    toggle()
    setIds({
      departmentIds: [],
      userIds: [],
    })
  }

  const onOk = () => {
    invite({
      variables: {
        followeeGroupId: group.groupId,
        followerUserIds: ids.userIds,
        departmentIds: ids.departmentIds,
      },
    })
    onClose()
  }

  return (
    <>
      {React.cloneElement(children as any, {
        onClick: toggle,
      })}
      <Modal
        bodyStyle={{ padding: 0 }}
        title={t('group.addUser')}
        visible={visible}
        onCancel={onClose}
        onOk={onOk}
        destroyOnClose={true}
      >
        <DepartmentWithSearch ids={ids} onUpdateIds={(ids) => setIds(ids)} />
      </Modal>
    </>
  )
}
